import React, { useEffect, useRef, useState } from 'react'
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap'
import PropTypes from 'prop-types'
import { useField, ErrorMessage } from 'formik'
import classnames from 'classnames'
import { InfoIcon } from '@abroad/components'
import ExperienceFieldLabel from '../Quests/ExperienceFieldLabel'
import { truncateVisibleChars } from '../Quests/enrollmentValidation'

/**
 * New-experience input / textarea for Build accordions.
 * Same props & Formik behaviour as InputField — styled for the v7 UI.
 * Keep InputField unchanged elsewhere until a global swap is approved.
 */
const ExperienceInputField = React.forwardRef(
  (
    {
      formGroupProps: {
        className: formGroupClassName,
        ...otherFormGroupProps
      } = {},
      children,
      id,
      label,
      labelProps,
      renderChildren,
      className,
      displayFlex = false,
      showTooltip,
      customErrorMessage,
      showErrorMessage,
      type,
      placeholder,
      removeFormikField,
      dataTooltip,
      showTooltipMessage = false,
      extraIcon,
      onFocus,
      onBlur,
      onChange: onChangeProp,
      hasErrorMessagePadding = true,
      errorVisible,
      renderLink = false,
      linkWrapperClasses = '',
      linkLabel = 'Delete',
      labelclassName = '',
      linkClickHandler = () => {},
      inputClasses = '',
      divClasses = '',
      as,
      rows,
      capitalizeFirstLetter = false,
      where = [],
      hint,
      hintClassName = '',
      maxLength,
      countVisibleChars = false,
      ...rest
    },
    ref,
  ) => {
    const [field, meta, helpers] = useField({ ...rest, ...{ type: type } })
    const [showPassword, setShowPassword] = useState(false)
    const [renderPasswordShowIcon, setRenderPasswordShowIcon] = useState(false)
    const textareaRef = useRef(null)
    const internalRef = ref || textareaRef
    const isTextarea = as === 'textarea'

    const autoResize = (element) => {
      if (element && isTextarea) {
        element.style.height = 'auto'
        element.style.height = `${element.scrollHeight}px`
      }
    }

    useEffect(() => {
      if (isTextarea && internalRef.current) {
        autoResize(internalRef.current)
      }
    }, [field.value, isTextarea, internalRef])

    const handleInputChange = (e) => {
      let value = e.target.value
      if (capitalizeFirstLetter && value) {
        value = value.charAt(0).toUpperCase() + value.slice(1)
      }
      if (countVisibleChars && typeof maxLength === 'number') {
        value = truncateVisibleChars(value, maxLength)
      }
      if (typeof onChangeProp === 'function') {
        onChangeProp({
          ...e,
          target: {
            ...e.target,
            name: e.target.name || rest.name || field.name,
            value,
          },
        })
      } else {
        helpers.setValue(value)
      }
      if (isTextarea) {
        autoResize(e.target)
      }
    }

    const renderErrorMessages = () => {
      if (!showErrorMessage || errorVisible === false) {
        return null
      }
      if (customErrorMessage) {
        return customErrorMessage
      }
      return (
        <Form.Text
          className={classnames('experience-f-error', {
            'px-1': hasErrorMessagePadding,
          })}>
          <ErrorMessage
            component='span'
            name={rest?.name}
            className={classnames(
              'text-error d-flex justify-content-end',
              { 'px-1': hasErrorMessagePadding },
              className,
            )}
          />
        </Form.Text>
      )
    }

    const onFocusField = (e) => {
      if (rest?.name === 'password') {
        helpers.setTouched(true, true)
      }
      if (type === 'password') {
        setRenderPasswordShowIcon(true)
      }
      if (onFocus) {
        onFocus(e)
      }
      helpers.setTouched(true, true)
      if (isTextarea) {
        autoResize(e.target)
      }
    }

    const onBlurField = (e) => {
      field.onBlur(e)
      if (onBlur) {
        onBlur(e)
      }
    }

    const renderPasswordIcon = () => {
      if (type === 'password' && renderPasswordShowIcon) {
        return (
          <div className='position-absolute eye-icon'>
            {showPassword ? (
              <button
                type='button'
                className='experience-muted-btn'
                onClick={() => setShowPassword(false)}
                aria-label='Hide password'>
                Hide
              </button>
            ) : (
              <button
                type='button'
                className='experience-muted-btn'
                onClick={() => setShowPassword(true)}
                aria-label='Show password'>
                Show
              </button>
            )}
          </div>
        )
      }
      return null
    }

    const renderInputLink = () => {
      if (!renderLink) {
        return null
      }
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          className={classnames(
            'position-absolute eye-icon',
            linkWrapperClasses,
          )}
          // eslint-disable-next-line no-script-url
          href='javascript:void(0)'
          onClick={linkClickHandler}>
          {linkLabel}
        </a>
      )
    }

    const renderInfoIcon = () => {
      if (rest?.showinfoicon !== true) {
        return null
      }
      return (
        <OverlayTrigger
          key='right'
          placement='right'
          overlay={
            <Tooltip id={`tooltip-${rest.name}`} className='font-open-sans'>
              This code will be used in the unique invite link for this
              organization/network
            </Tooltip>
          }>
          <div className='position-absolute code-icon'>
            <InfoIcon />
          </div>
        </OverlayTrigger>
      )
    }

    const labelContent =
      label && where.length > 0 ? (
        <ExperienceFieldLabel where={where}>{label}</ExperienceFieldLabel>
      ) : (
        label
      )

    const renderLabel = () => {
      if (!label) {
        return null
      }
      const labelClass = classnames(
        'experience-f-label',
        labelProps?.className,
        labelclassName,
        { 'cursor-pointer': showTooltipMessage },
      )
      if (showTooltipMessage) {
        return (
          <OverlayTrigger
            key='top'
            placement='top'
            overlay={
              <Tooltip id={`tooltip-${rest.name}`} className='font-open-sans'>
                <div dangerouslySetInnerHTML={{ __html: dataTooltip }} />
              </Tooltip>
            }>
            <Form.Label {...labelProps} className={labelClass}>
              {labelContent}
              {extraIcon}
            </Form.Label>
          </OverlayTrigger>
        )
      }
      return (
        <Form.Label {...labelProps} className={labelClass}>
          {labelContent}
          {extraIcon}
        </Form.Label>
      )
    }

    // When counting visible chars, do not rely on HTML maxlength (UTF-16).
    const nativeMaxLength =
      countVisibleChars && typeof maxLength === 'number' ? undefined : maxLength

    return (
      <div className='experience-f'>
        <Form.Group
          controlId={id || `experience-control-${rest?.name}`}
          className={classnames('mb-0', formGroupClassName)}
          {...otherFormGroupProps}>
          <span className='d-flex position-relative align-items-center'>
            <div
              className={classnames(
                'align-items-center w-100',
                {
                  'd-lg-flex': displayFlex,
                },
                divClasses,
              )}>
              {renderLabel()}
              <Form.Control
                ref={internalRef}
                as={as}
                rows={rows}
                className={classnames(
                  // Avoid legacy .user-input / .border-radius-5 — they force
                  // 12px / weight 600 / 5px radius and fight the v7 field chrome.
                  'experience-f-control transition abroad-fs-exclude',
                  {
                    'eye-icon':
                      (type === 'password' && renderPasswordShowIcon) ||
                      rest?.showinfoicon === true,
                    'experience-f-control-tall': isTextarea,
                  },
                  className,
                  inputClasses,
                )}
                // Never use Bootstrap's green "is-valid" chrome in the new UI
                isValid={false}
                isInvalid={
                  errorVisible !== false &&
                  ((meta.touched && !!meta.error) ||
                    (showErrorMessage && !!meta.error))
                }
                type={type === 'password' && showPassword ? 'text' : type}
                placeholder={placeholder}
                maxLength={nativeMaxLength}
                {...rest}
                {...(!removeFormikField
                  ? {
                      name: field.name,
                      value: field.value ?? '',
                    }
                  : {})}
                onFocus={onFocusField}
                onBlur={onBlurField}
                onChange={handleInputChange}>
                {children || renderChildren}
              </Form.Control>
            </div>
            {renderPasswordIcon()}
            {renderInfoIcon()}
            {renderInputLink()}
          </span>
          {renderErrorMessages()}
          {hint ? (
            <p className={classnames('experience-f-hint', hintClassName)}>
              {hint}
            </p>
          ) : null}
        </Form.Group>
      </div>
    )
  },
)

ExperienceInputField.defaultProps = {
  showTooltip: false,
  removeFormikField: false,
  type: 'text',
  showErrorMessage: false,
  where: [],
  countVisibleChars: false,
}

ExperienceInputField.propTypes = {
  removeFormikField: PropTypes.bool,
  showErrorMessage: PropTypes.bool,
  as: PropTypes.string,
  rows: PropTypes.number,
  capitalizeFirstLetter: PropTypes.bool,
  where: PropTypes.arrayOf(PropTypes.string),
  hint: PropTypes.node,
  hintClassName: PropTypes.string,
  errorVisible: PropTypes.bool,
  label: PropTypes.node,
  maxLength: PropTypes.number,
  countVisibleChars: PropTypes.bool,
}

ExperienceInputField.displayName = 'ExperienceInputField'

export default ExperienceInputField
